import { useState } from 'react';
import { Form as HookForm, useForm } from 'react-hook-form';
import { Box, Flex } from '@chakra-ui/react';
import Cookies from 'universal-cookie';
import { useNavigate, useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { ArrowRightIcon, CloseIcon, MicrophoneIcon } from 'Asset/icon';
import { TextBold, TextRegular } from 'UiKit/typography';
import InputSelect from 'UiKit/input-select';
import InputTextAreaComponent from 'UiKit/input-textarea';
import ButtonSolid from 'UiKit/button/buttonSolid';
import { useAppSystem } from 'Store/app';
import useGetStudioList from 'Domain/host-help-partner/useGetStudioList';
import usePostHelpRequest from 'Domain/host-help-partner/usePostHelpRequest';

import { defaultValues } from 'View/host-help-partner/const/form';

import styles from './styles.module.css';

const Form = () => {
  const { t } = useTranslation('host-help-partner');
  const [notes, setNotes] = useState('');

  const { onChangeAppSystem } = useAppSystem();
  const cookie = new Cookies();
  const navigate = useNavigate();
  const { key } = useLocation();
  const username = cookie.get('username');

  const closeTarget = key !== 'default' ? -1 : '/host';

  const { watch, control, handleSubmit, setValue } = useForm(defaultValues);

  const { studioId } = watch();

  const { data: studioOptions } = useGetStudioList();
  const { mutate: onPostHelpRequest } = usePostHelpRequest();

  const onSubmit = () => onPostHelpRequest({ studioId: studioId?.value, notes });

  const onClose = () => {
    navigate(closeTarget);
    onChangeAppSystem({ isHideMobileNavBar: false });
  };

  return (
    <Box className={styles['form__container']}>
      {/* Head */}

      <Box className={styles['form__section--head']}>
        <CloseIcon size="18px" onClick={onClose} className={styles['form__head-icon']} />
      </Box>

      <HookForm control={control} onSubmit={handleSubmit(onSubmit)}>
        {/* Content */}

        <Box className={styles['form__section--content']}>
          <Box className={styles['form__container--text']}>
            <TextBold className={styles['form__text--title']}>{t('asking_for_help.title')}</TextBold>
            <TextRegular className={styles['form__text--description']}>{t('asking_for_help.description')}</TextRegular>
          </Box>

          <Box className={styles['form-info__container']}>
            <MicrophoneIcon size="16px" color="var(--color-neutral60)" />
            <Flex direction={'column'} gap={'4px'}>
              <TextRegular className={styles['form__host-label']}>Host</TextRegular>
              <TextBold>{username}</TextBold>
            </Flex>
          </Box>

          <Box>
            <InputSelect
              label={t('asking_for_help.room_label')}
              isRequired
              placeholder={t('asking_for_help.room_placeholder')}
              titleClassName={styles['form__label-title']}
              options={studioOptions}
              onChange={(value) => setValue('studioId', value)}
              value={studioId}
            />
          </Box>

          <Box className={styles['form-textarea__container']}>
            <Flex className={styles['form-textarea__container--head']}>
              <TextBold className={styles['form-textarea__label']}>{t('asking_for_help.notes_label')}</TextBold>
              <TextRegular className={styles['form-textarea__count']}>({notes?.length}/100)</TextRegular>
            </Flex>
            <InputTextAreaComponent
              value={notes}
              className={styles['form-textarea__textarea']}
              placeholder={t('asking_for_help.notes_placeholder')}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={100}
            />
          </Box>
        </Box>

        {/* Footer */}

        <Box className={styles['form__section--footer']}>
          <ButtonSolid className={styles['form__button']} type="submit" disabled={!studioId}>
            {t('asking_for_help.title')}
            <ArrowRightIcon size="18px" />
          </ButtonSolid>
        </Box>
      </HookForm>
    </Box>
  );
};

export default Form;
